/**
 * The sync orchestrator: drains local changes up to Supabase and pulls remote
 * changes down, reconciling by room (ADR 0019/0022).
 *
 * Model, kept deliberately simple for the single-user / multi-device backup
 * case the owner asked for (not real-time collaboration):
 *   - DIRTY SET: room ids changed locally since their last successful push,
 *     persisted so an edit made offline survives an app restart.
 *   - CURSOR: the newest server `updated_at` pulled so far; the next pull asks
 *     only for rooms changed after it.
 *   - STREAM STAMP: which (Supabase URL, user id) the cursors and init flags
 *     belong to. A watermark consulted against any OTHER stream silently skips
 *     every row at or below it — so on mismatch (or no stamp at all, which is
 *     an unverifiable cursor) the engine refuses the stored cursors and init
 *     flags and starts from null: it degrades to over-pulling, never to
 *     skipping. The dirty set is deliberately NOT cleared — dropping unpushed
 *     edits silently is worse than surfacing push errors. See ADR 0102.
 *   - PUSH dirty rooms, then PULL rooms after the cursor and write each back
 *     locally UNLESS it is still dirty (a local edit not yet pushed wins).
 *
 * All side-effecting collaborators are injected, so the whole flow is unit
 * tested with fakes and no network. supabase-js stays behind `getSupabase`.
 *
 * @param {object} deps
 * @param {string} deps.streamKey                which database this engine talks to
 *   (the Supabase URL in production). Half of the stream stamp; required so an
 *   engine can never write cursors it cannot later verify.
 * @param {() => object} deps.getSupabase        the shared client
 * @param {{currentUser:Function}} deps.auth
 * @param {object} deps.db                        local-db surface (get/put/list/meta)
 * @param {Function} deps.deriveProjectId
 * @param {Function} deps.sketchToRows
 * @param {Function} deps.rowsToSketch
 * @param {Function} deps.pushRoom
 * @param {Function} deps.pullRooms
 * @param {(status:object)=>void} [deps.onStatus] progress callback for the UI
 * @param {() => string} [deps.makeId]
 */
export function createSyncEngine(deps) {
  const {
    streamKey, getSupabase, auth, db, deriveProjectId, sketchToRows, rowsToSketch, pushRoom, pullRooms, pushRoomDelete,
    remeasureSessionToRows, rowToRemeasureSession, pushRemeasure, pullRemeasures,
    measurementSessionToRows, rowToMeasurementSession, pushMeasurementSession, pullMeasurementSessions,
    sectionLibrary, componentToRow, rowToComponent, pushReusableSection, pullReusableSections,
    onStatus = () => {}, makeId = () => crypto.randomUUID(),
  } = deps;
  if (typeof streamKey !== 'string' || streamKey.length === 0) {
    throw new TypeError('sync-engine: streamKey (the Supabase URL) is required — an engine without one writes cursors no later run can verify.');
  }

  const DIRTY_KEY = 'syncDirtyIds';
  const PENDING_DELETE_KEY = 'syncPendingDeleteIds';
  const CURSOR_KEY = 'syncCursor';
  const CLIENT_KEY = 'syncClientId';
  const INIT_KEY = 'syncInitialized';
  const STREAM_KEY = 'syncStream';
  const REMEASURE_DIRTY_KEY = 'syncRemeasureDirtyIds';
  const REMEASURE_CURSOR_KEY = 'syncRemeasureCursor';
  const REMEASURE_INIT_KEY = 'syncRemeasureInitialized';
  const MEASUREMENT_DIRTY_KEY = 'syncMeasurementDirtyIds';
  const MEASUREMENT_CURSOR_KEY = 'syncMeasurementCursor';
  const MEASUREMENT_INIT_KEY = 'syncMeasurementSessionsInitialized';
  const REUSABLE_DIRTY_KEY = 'syncReusableSectionsDirtyIds';
  const REUSABLE_CURSOR_KEY = 'syncReusableSectionsCursor';
  const REUSABLE_INIT_KEY = 'syncReusableSectionsInitialized';

  let inFlight = null; // coalesce concurrent syncNow() calls
  let dirty = null; // in-memory mirror of the persisted dirty set (a Set)
  let remeasureDirty = null;
  const remeasureSyncEnabled = !!(
    db.getRemeasureSession && db.putRemeasureSession && db.listRemeasureSessions
    && remeasureSessionToRows && rowToRemeasureSession && pushRemeasure && pullRemeasures
  );
  const reusableSyncEnabled = !!(
    sectionLibrary && componentToRow && rowToComponent
    && pushReusableSection && pullReusableSections
  );
  let reusableDirty = null;
  const measurementSyncEnabled = !!(
    db.getMeasurementSession && db.putMeasurementSession && db.listMeasurementSessions
    && db.deleteMeasurementSession
    && measurementSessionToRows && rowToMeasurementSession
    && pushMeasurementSession && pullMeasurementSessions
  );
  let measurementDirty = null;

  async function loadDirty() {
    if (dirty) return dirty;
    const stored = await db.getMeta(DIRTY_KEY);
    dirty = new Set(Array.isArray(stored) ? stored : []);
    return dirty;
  }

  async function persistDirty() {
    await db.setMeta(DIRTY_KEY, [...(dirty ?? [])]);
  }

  async function loadRemeasureDirty() {
    if (remeasureDirty) return remeasureDirty;
    const stored = await db.getMeta(REMEASURE_DIRTY_KEY);
    remeasureDirty = new Set(Array.isArray(stored) ? stored : []);
    return remeasureDirty;
  }

  async function persistRemeasureDirty() {
    await db.setMeta(REMEASURE_DIRTY_KEY, [...(remeasureDirty ?? [])]);
  }

  /** Mark a room as needing a push (called by the app after every save). */
  async function markDirty(id) {
    if (!id) return;
    const set = await loadDirty();
    set.add(id);
    await persistDirty();
  }

  let pendingDeletes = null; // in-memory mirror, same lifecycle as `dirty`

  async function loadPendingDeletes() {
    if (pendingDeletes) return pendingDeletes;
    const stored = await db.getMeta(PENDING_DELETE_KEY);
    pendingDeletes = new Set(Array.isArray(stored) ? stored : []);
    return pendingDeletes;
  }

  async function persistPendingDeletes() {
    await db.setMeta(PENDING_DELETE_KEY, [...(pendingDeletes ?? [])]);
  }

  /**
   * Record a local room delete for propagation (A5, ADR 0104). The doc is
   * already gone from IndexedDB, so this is the ONLY trace the intent leaves —
   * the push loop's "gone locally; nothing to push" is exactly how deletes
   * used to vanish. Persisted like the dirty set: an offline delete survives
   * restarts until a sync delivers it. Also un-dirties the id — a deleted
   * room has no content push to make.
   */
  async function markDeleted(id) {
    if (!id) return;
    const deletes = await loadPendingDeletes();
    deletes.add(id);
    await persistPendingDeletes();
    const set = await loadDirty();
    if (set.delete(id)) await persistDirty();
  }

  /** Mark a separate Engineering remeasure record as needing cloud backup. */
  async function markRemeasureDirty(id) {
    if (!id || !remeasureSyncEnabled) return;
    const set = await loadRemeasureDirty();
    set.add(id);
    await persistRemeasureDirty();
  }

  async function loadMeasurementDirty() {
    if (measurementDirty) return measurementDirty;
    const stored = await db.getMeta(MEASUREMENT_DIRTY_KEY);
    measurementDirty = new Set(Array.isArray(stored) ? stored : []);
    return measurementDirty;
  }

  async function persistMeasurementDirty() {
    await db.setMeta(MEASUREMENT_DIRTY_KEY, [...(measurementDirty ?? [])]);
  }

  /** Mark a field measurement session as needing cloud backup (N1, ADR 0105). */
  async function markMeasurementSessionDirty(id) {
    if (!id || !measurementSyncEnabled) return;
    const set = await loadMeasurementDirty();
    set.add(id);
    await persistMeasurementDirty();
  }

  async function loadReusableDirty() {
    if (reusableDirty) return reusableDirty;
    const stored = await db.getMeta(REUSABLE_DIRTY_KEY);
    reusableDirty = new Set(Array.isArray(stored) ? stored : []);
    return reusableDirty;
  }

  async function persistReusableDirty() {
    await db.setMeta(REUSABLE_DIRTY_KEY, [...(reusableDirty ?? [])]);
  }

  /** Mark a saved reusable-section component as needing cloud backup (D97). */
  async function markReusableSectionDirty(id) {
    if (!id || !reusableSyncEnabled) return;
    const set = await loadReusableDirty();
    set.add(id);
    await persistReusableDirty();
  }

  async function ensureClientId() {
    let id = await db.getMeta(CLIENT_KEY);
    if (!id) {
      id = makeId();
      await db.setMeta(CLIENT_KEY, id);
    }
    return id;
  }

  /**
   * The push half, shared verbatim by syncNow() and flushNow() so the two can
   * never drift: dirty rooms (content), pending room deletes (A5 tombstone
   * pushes — after content on purpose: a delete raced against edits still
   * converges), remeasures (after rooms so room_id's FK exists), and
   * measurement sessions (project identity read through the room's SKETCH —
   * a session whose sketch is gone fails LOUDLY and stays dirty:
   * refuse-don't-drop, a dropped push is a field reading nobody can retake).
   */
  async function pushAllDirty(supabase, user, clientId, errors) {
    const set = await loadDirty();
    for (const id of [...set]) {
      const doc = await db.getSketch(id);
      if (!doc) {
        set.delete(id); // gone locally; nothing to push
        continue;
      }
      const projectId = await deriveProjectId(user.id, doc.client, doc.opportunity);
      const rows = sketchToRows(doc, { ownerId: user.id, projectId, clientId });
      const res = await pushRoom(supabase, rows);
      if (res.ok) set.delete(id);
      else errors.push(res.error);
    }
    await persistDirty();

    const deletes = pushRoomDelete ? await loadPendingDeletes() : new Set();
    for (const id of [...deletes]) {
      const res = await pushRoomDelete(supabase, id);
      if (res.ok) deletes.delete(id);
      else errors.push(res.error);
    }
    if (pushRoomDelete) await persistPendingDeletes();

    if (remeasureSyncEnabled) {
      const remeasureSet = await loadRemeasureDirty();
      for (const id of [...remeasureSet]) {
        const session = await db.getRemeasureSession(id);
        if (!session) {
          remeasureSet.delete(id);
          continue;
        }
        const source = session.originalSketch;
        const projectId = await deriveProjectId(user.id, source.client, source.opportunity);
        const rows = remeasureSessionToRows(session, { ownerId: user.id, projectId, clientId });
        const result = await pushRemeasure(supabase, rows);
        if (result.ok) remeasureSet.delete(id);
        else errors.push(result.error);
      }
      await persistRemeasureDirty();
    }

    if (measurementSyncEnabled) {
      const measurementSet = await loadMeasurementDirty();
      for (const id of [...measurementSet]) {
        const session = await db.getMeasurementSession(id);
        if (!session) {
          measurementSet.delete(id); // gone locally; nothing to push
          continue;
        }
        const sketchDoc = session.roomId ? await db.getSketch(session.roomId) : null;
        if (!sketchDoc) {
          errors.push(`measurement session ${id}: its room sketch is missing — cannot derive the owning project`);
          continue;
        }
        const projectId = await deriveProjectId(user.id, sketchDoc.client, sketchDoc.opportunity);
        const projectName = [sketchDoc.client, sketchDoc.opportunity]
          .map((v) => String(v ?? '').trim()).filter(Boolean).join(' — ') || 'Untitled project';
        const rows = measurementSessionToRows(session, { ownerId: user.id, projectId, clientId, projectName });
        const result = await pushMeasurementSession(supabase, rows);
        if (result.ok) measurementSet.delete(id);
        else errors.push(result.error);
      }
      await persistMeasurementDirty();
    }
  }

  /**
   * The push-only durability flush (N1(b), Coordinator ruling 2026-08-29).
   * Fired when the page is about to leave the foreground — the moment it is
   * most likely to be frozen or discarded — so it is the MINIMUM that gets
   * data off the device: dirty-gated (zero requests on an incidental
   * backgrounding), pushes only, no pulls, no init backfill, no cursor or
   * stream writes. Reusable-section components are deliberately excluded:
   * they are design-kit state, not field readings, and drain on the next
   * full sync.
   */
  async function flushNow() {
    const user = await auth.currentUser();
    if (!user) return { ok: true, flushed: false };
    const dirtyWork = (await loadDirty()).size
      + (pushRoomDelete ? (await loadPendingDeletes()).size : 0)
      + (remeasureSyncEnabled ? (await loadRemeasureDirty()).size : 0)
      + (measurementSyncEnabled ? (await loadMeasurementDirty()).size : 0);
    if (dirtyWork === 0) return { ok: true, flushed: false };
    const supabase = getSupabase();
    const clientId = await ensureClientId();
    const errors = [];
    await pushAllDirty(supabase, user, clientId, errors);
    if (errors.length) return { ok: false, errors };
    return { ok: true, flushed: true };
  }

  function emit(state, extra = {}) {
    onStatus({ state, ...extra });
  }

  /** Run a full push+pull cycle. Coalesces if one is already running. */
  function syncNow() {
    if (inFlight) return inFlight;
    inFlight = run().finally(() => {
      inFlight = null;
    });
    return inFlight;
  }

  async function run() {
    const user = await auth.currentUser();
    if (!user) {
      emit('signed-out');
      return { ok: false, reason: 'signed-out' };
    }
    const supabase = getSupabase();

    // STREAM STAMP — must run before anything reads a cursor or init flag.
    // The stored watermarks are only meaningful against the exact stream that
    // advanced them: this database (streamKey) AND this signed-in user (the
    // pull is owner-global under RLS, so a different user is a different
    // stream). On any mismatch — including a cursor written by a pre-stamp
    // build, whose stream is unprovable — refuse the watermarks and the
    // "already backed up" flags and start over: the failure direction becomes
    // one over-pull/over-push, never a silent permanent skip. The dirty set
    // survives on purpose (see the header). ADR 0102.
    const stream = `${streamKey}::${user.id}`;
    if ((await db.getMeta(STREAM_KEY)) !== stream) {
      await db.setMeta(CURSOR_KEY, null);
      await db.setMeta(REMEASURE_CURSOR_KEY, null);
      await db.setMeta(REUSABLE_CURSOR_KEY, null);
      await db.setMeta(INIT_KEY, null);
      await db.setMeta(REMEASURE_INIT_KEY, null);
      await db.setMeta(MEASUREMENT_CURSOR_KEY, null);
      await db.setMeta(MEASUREMENT_INIT_KEY, null);
      await db.setMeta(REUSABLE_INIT_KEY, null);
      await db.setMeta(STREAM_KEY, stream);
    }

    const clientId = await ensureClientId();
    const set = await loadDirty();
    const remeasureSet = remeasureSyncEnabled ? await loadRemeasureDirty() : new Set();
    const measurementSet = measurementSyncEnabled ? await loadMeasurementDirty() : new Set();

    // First sync on this device: back up everything already drawn here, not
    // just future edits. Sketches with no walls (fresh blanks) are skipped —
    // there is nothing to preserve. Runs once, guarded by a meta flag.
    if (!(await db.getMeta(INIT_KEY))) {
      for (const d of await db.listSketches()) {
        if (Array.isArray(d.walls) && d.walls.length > 0) set.add(d.id);
      }
      await db.setMeta(INIT_KEY, true);
      await persistDirty();
    }
    if (remeasureSyncEnabled && !(await db.getMeta(REMEASURE_INIT_KEY))) {
      for (const session of await db.listRemeasureSessions()) remeasureSet.add(session.id);
      await db.setMeta(REMEASURE_INIT_KEY, true);
      await persistRemeasureDirty();
    }
    // Every existing session backs up on first sync — readings taken BEFORE
    // this wiring existed are the ones the owner called "the most important
    // thing to back up" (N1); an empty shell pushes harmlessly.
    if (measurementSyncEnabled && !(await db.getMeta(MEASUREMENT_INIT_KEY))) {
      for (const session of await db.listMeasurementSessions()) measurementSet.add(session.id);
      await db.setMeta(MEASUREMENT_INIT_KEY, true);
      await persistMeasurementDirty();
    }
    emit('syncing');

    const errors = [];
    // Room ids whose tombstone arrived in THIS sync's pull. Reported on the
    // 'synced' status so the app can react precisely when the OPEN sketch was
    // deleted elsewhere — a "doc missing locally?" heuristic instead would
    // misfire on a fresh sketch that simply has not hit its first auto-save.
    const tombstonedRoomIds = [];

    // The push half is shared with flushNow(): rooms, room deletes,
    // remeasures, measurement sessions — see pushAllDirty below.
    await pushAllDirty(supabase, user, clientId, errors);

    // ---- PULL remote changes ---------------------------------------------
    const cursor = (await db.getMeta(CURSOR_KEY)) ?? null;
    const pull = await pullRooms(supabase, cursor);
    let pulled = 0;
    if (!pull.ok) {
      errors.push(pull.error);
    } else {
      let newCursor = cursor;
      for (const bundle of pull.rooms) {
        const roomId = bundle.room.id;
        if (bundle.room.deleted_at) {
          tombstonedRoomIds.push(roomId);
          // A tombstone (A5, ADR 0104). It WINS over a local dirty edit —
          // the race has no knowable ordering, consistency outranks either
          // candidate, and deletion is the one intent a merge cannot
          // express. The dirty flag dies with the room; a pending delete
          // for the same id is settled too (deleted on both devices, the
          // remote one landed first).
          if (db.deleteSketch && await db.getSketch(roomId)) {
            await db.deleteSketch(roomId);
            pulled += 1;
          }
          if (set.delete(roomId)) await persistDirty();
          const pending = await loadPendingDeletes();
          if (pending.delete(roomId)) await persistPendingDeletes();
        } else if (!set.has(roomId)) {
          // A room still dirty locally has an unpushed edit — don't clobber it.
          await db.putSketch(rowsToSketch(bundle));
          pulled += 1;
        }
        const ts = bundle.room.updated_at;
        if (ts && (!newCursor || ts > newCursor)) newCursor = ts;
      }
      if (newCursor && newCursor !== cursor) await db.setMeta(CURSOR_KEY, newCursor);
    }

    if (remeasureSyncEnabled) {
      const remeasureCursor = (await db.getMeta(REMEASURE_CURSOR_KEY)) ?? null;
      const remeasurePull = await pullRemeasures(supabase, remeasureCursor);
      if (!remeasurePull.ok) {
        errors.push(remeasurePull.error);
      } else {
        let newCursor = remeasureCursor;
        for (const row of remeasurePull.rows) {
          if (!remeasureSet.has(row.id)) {
            await db.putRemeasureSession(rowToRemeasureSession(row));
            pulled += 1;
          }
          if (row.updated_at && (!newCursor || row.updated_at > newCursor)) newCursor = row.updated_at;
        }
        if (newCursor && newCursor !== remeasureCursor) await db.setMeta(REMEASURE_CURSOR_KEY, newCursor);
      }
    }

    if (measurementSyncEnabled) {
      const measurementCursor = (await db.getMeta(MEASUREMENT_CURSOR_KEY)) ?? null;
      const measurementPull = await pullMeasurementSessions(supabase, measurementCursor);
      if (!measurementPull.ok) {
        errors.push(measurementPull.error);
      } else {
        let newCursor = measurementCursor;
        for (const row of measurementPull.rows) {
          if (row.deleted_at) {
            // The A5 tombstone pattern, one shape across entities: remove
            // locally, advance the cursor. (No delete control exists in the
            // app yet, but a tombstone written any other way must still land.)
            if (await db.getMeasurementSession(row.id)) {
              await db.deleteMeasurementSession(row.id);
              pulled += 1;
            }
          } else if (!measurementSet.has(row.id)) {
            // A dirty local session has unpushed readings — don't clobber.
            await db.putMeasurementSession(rowToMeasurementSession(row));
            pulled += 1;
          }
          if (row.updated_at && (!newCursor || row.updated_at > newCursor)) newCursor = row.updated_at;
        }
        if (newCursor && newCursor !== measurementCursor) await db.setMeta(MEASUREMENT_CURSOR_KEY, newCursor);
      }
    }

    // ---- REUSABLE SECTIONS (D97, ADR 0100 amendment) ----------------------
    // The designer's own kit, owner-scoped, no project hop. The WHOLE entity
    // is skipped unless the library loaded: its storage is one whole-replaced
    // key, so pushing from — or merging into — a library we could not read
    // would guess at what it is overwriting. The panel already surfaces the
    // load failure; the entity simply waits for a readable library.
    if (reusableSyncEnabled && sectionLibrary.status().state === 'ready') {
      const reusableSet = await loadReusableDirty();
      // First sync: back up every component saved before the wiring existed.
      if (!(await db.getMeta(REUSABLE_INIT_KEY))) {
        for (const component of sectionLibrary.list()) reusableSet.add(component.id);
        await db.setMeta(REUSABLE_INIT_KEY, true);
        await persistReusableDirty();
      }
      for (const id of [...reusableSet]) {
        const component = sectionLibrary.get(id);
        if (!component) {
          reusableSet.delete(id); // gone locally; nothing to push
          continue;
        }
        const mapped = componentToRow(component, { ownerId: user.id });
        if (!mapped.ok) {
          // Invalid components cannot be produced by the library's own add(),
          // so this is surfaced loudly rather than silently dropped.
          errors.push(`reusable section "${component.name}": ${mapped.reason}`);
          continue;
        }
        const res = await pushReusableSection(supabase, mapped.row);
        if (res.ok) reusableSet.delete(id);
        else errors.push(res.error);
      }
      await persistReusableDirty();

      const reusableCursor = (await db.getMeta(REUSABLE_CURSOR_KEY)) ?? null;
      const reusablePull = await pullReusableSections(supabase, reusableCursor);
      if (!reusablePull.ok) {
        errors.push(reusablePull.error);
      } else {
        // Tombstones ride the same pull and remove the component locally —
        // the only way a deletion can reach this device (the cursor filter
        // that hid them was falsified while wiring; see sync-client.js).
        const applied = await sectionLibrary.applyRemote(
          reusablePull.rows.map(rowToComponent).filter(Boolean),
        );
        if (!applied.ok) {
          // Cursor deliberately NOT advanced: rows the library refused would
          // otherwise be skipped forever — same refuse-don't-proceed posture
          // as a failed page (#103).
          errors.push(applied.reason);
        } else {
          pulled += applied.changed;
          let newCursor = reusableCursor;
          for (const row of reusablePull.rows) {
            if (row.updated_at && (!newCursor || row.updated_at > newCursor)) newCursor = row.updated_at;
          }
          if (newCursor && newCursor !== reusableCursor) await db.setMeta(REUSABLE_CURSOR_KEY, newCursor);
        }
      }
    }

    if (errors.length) {
      emit('error', { message: errors[0], pending: set.size + remeasureSet.size });
      return { ok: false, errors, pulled };
    }
    emit('synced', { pulled, pending: set.size + remeasureSet.size, tombstonedRoomIds });
    return { ok: true, pulled, pushed: true };
  }

  return { markDirty, markDeleted, markRemeasureDirty, markMeasurementSessionDirty, markReusableSectionDirty, syncNow, flushNow };
}
